(() => {
  const DESKTOP = () => window.matchMedia('(min-width: 750px)').matches;

  const init = (header) => {
    if (header.dataset.aeyaHeaderReady) return;
    header.dataset.aeyaHeaderReady = 'true';

    const panelItems = [...header.querySelectorAll('[data-panel-trigger]')];

    // Re-resolve per use — the cart's section-hydration clones the header; ':scope >' skips the stale [data-drawer] clone.
    const realDrawer = () => header.querySelector(':scope > [data-drawer]');
    const realScrim = () => header.querySelector(':scope > [data-scrim]');
    const menuToggleEl = () => header.querySelector('.aeya-header__menu-toggle');

    const show = (el) => { if (el) el.hidden = false; };
    const hide = (el) => { if (el) el.hidden = true; };

    const lockScroll = (on) => {
      document.documentElement.style.overflow = on ? 'hidden' : '';
    };

    let openItem = null;
    const syncPanelOpenFlag = () => {
      if (openItem) header.setAttribute('data-panel-open', '');
      else header.removeAttribute('data-panel-open');
    };
    const closePanel = (item) => {
      if (!item) return;
      item.removeAttribute('data-open');
      const btn = item.querySelector('.aeya-header__nav-link');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      if (openItem === item) openItem = null;
      syncPanelOpenFlag();
    };
    const openPanel = (item) => {
      if (openItem && openItem !== item) closePanel(openItem);
      item.setAttribute('data-open', '');
      const btn = item.querySelector('.aeya-header__nav-link');
      if (btn) btn.setAttribute('aria-expanded', 'true');
      openItem = item;
      syncPanelOpenFlag();
    };

    let panelCloseTimer = null;
    const cancelPanelClose = () => {
      if (panelCloseTimer) { window.clearTimeout(panelCloseTimer); panelCloseTimer = null; }
    };
    const schedulePanelClose = (item) => {
      cancelPanelClose();
      panelCloseTimer = window.setTimeout(() => closePanel(item), 220);
    };

    panelItems.forEach((item) => {
      item.addEventListener('mouseenter', () => { if (DESKTOP()) { cancelPanelClose(); openPanel(item); } });
      item.addEventListener('mouseleave', () => { if (DESKTOP()) schedulePanelClose(item); });
      item.addEventListener('focusin', () => { if (DESKTOP()) { cancelPanelClose(); openPanel(item); } });
      item.addEventListener('focusout', (e) => {
        if (DESKTOP() && !item.contains(e.relatedTarget)) schedulePanelClose(item);
      });
    });

    const isDrawerOpen = () => { const d = realDrawer(); return !!d && d.hasAttribute('data-open'); };
    const closeDrawer = () => {
      const drawer = realDrawer();
      if (!drawer || !drawer.hasAttribute('data-open')) return;
      const scrim = realScrim();
      drawer.removeAttribute('data-open');
      if (scrim) scrim.removeAttribute('data-open');
      menuToggleEl()?.setAttribute('aria-expanded', 'false');
      lockScroll(false);
      window.setTimeout(() => {
        const d = realDrawer();
        if (d && !d.hasAttribute('data-open')) {
          hide(d);
          hide(realScrim());
          header.removeAttribute('data-drawer-open');
        }
      }, 350);
    };
    const openDrawer = () => {
      const drawer = realDrawer();
      if (!drawer) return;
      const scrim = realScrim();
      header.dataset.scrollDirection = 'none';
      header.setAttribute('data-drawer-open', '');
      show(drawer);
      show(scrim);
      // force reflow so the slide-in transition runs from the closed state
      void drawer.offsetWidth;
      drawer.setAttribute('data-open', '');
      if (scrim) scrim.setAttribute('data-open', '');
      menuToggleEl()?.setAttribute('aria-expanded', 'true');
      lockScroll(true);
    };

    header.addEventListener('click', (e) => {
      if (e.target.closest('.aeya-header__menu-toggle')) {
        if (isDrawerOpen()) closeDrawer();
        else openDrawer();
        return;
      }
      if (e.target.closest('[data-drawer-close]') || e.target.closest('[data-scrim]')) {
        closeDrawer();
        return;
      }
      const accBtn = e.target.closest('[data-drawer-accordion]');
      if (accBtn) {
        const item = accBtn.closest('.aeya-header__drawer-item');
        if (item) {
          const open = item.hasAttribute('data-open');
          item.toggleAttribute('data-open', !open);
          accBtn.setAttribute('aria-expanded', String(!open));
        }
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (isDrawerOpen()) closeDrawer();
      else if (openItem) closePanel(openItem);
    });

    let lastScroll = document.scrollingElement?.scrollTop ?? 0;
    let rafId = null;

    const SOLID_THRESHOLD = 8;

    const overlayOpen = () => isDrawerOpen() || openItem != null;

    const updateScroll = () => {
      rafId = null;

      const scrollTop = document.scrollingElement?.scrollTop ?? 0;
      const isScrollingUp = scrollTop < lastScroll;
      const pastTop = scrollTop > SOLID_THRESHOLD;

      header.dataset.stickyState = pastTop ? 'active' : 'inactive';

      // Don't transform the header while a panel is open — transform makes it the containing block and breaks the anchored panel.
      if (overlayOpen()) {
        header.dataset.scrollDirection = 'none';
        lastScroll = scrollTop;
        return;
      }

      if (pastTop) {
        header.dataset.scrollDirection = isScrollingUp ? 'up' : 'down';
      } else {
        header.dataset.scrollDirection = 'none';
      }

      lastScroll = scrollTop;
    };

    const onScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(updateScroll);
    };

    document.addEventListener('scroll', onScroll, { passive: true });
    updateScroll();

    const isTransparent = header.classList.contains('aeya-header--transparent');
    const bar = header.querySelector('.aeya-header__bar');
    const group = document.getElementById('header-group');
    const setHeights = () => {
      const h = Math.round((bar || header).offsetHeight);
      document.body.style.setProperty('--header-height', `${h}px`);
      let groupH = 0;
      if (group) {
        [...group.children].forEach((child) => {
          if (child !== header.closest('.shopify-section')) groupH += child.offsetHeight || 0;
        });
        if (isTransparent && header.closest('.shopify-section')?.nextElementSibling) {
          groupH += h;
        }
      }
      document.body.style.setProperty('--header-group-height', `${groupH || h}px`);
    };
    setHeights();
    if ('ResizeObserver' in window) {
      new ResizeObserver(setHeights).observe(bar || header);
    }

    window.addEventListener('resize', () => {
      if (DESKTOP() && isDrawerOpen()) closeDrawer();
      if (!DESKTOP() && openItem) closePanel(openItem);
      setHeights();
    });
  };

  const boot = () => document.querySelectorAll('aeya-header').forEach(init);

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);

  document.addEventListener('shopify:section:load', (e) => {
    const header = e.target.querySelector('aeya-header');
    if (header) init(header);
  });
})();
